import {
  Alert,
  Box,
  Button,
  Chip,
  Link,
  Paper,
  Snackbar,
  Stack,
  Typography,
  useTheme
} from "@carrotfertility/carotene-core"
import React, { useState } from "react"
import { useIntl } from "react-intl"
import { AppointmentsAvatar } from "./AppointmentsAvatar"
import { getAppointmentTypeLabel } from "./appointmentTypeLabels"
import {
  formatAppointmentDate,
  formatAppointmentDateWithWeekday,
  formatAppointmentTime,
  formatCountdownLabel,
  formatProviderName
} from "./formatting"
import { perViewport, perViewportTypography } from "./responsive"
import { useAppointmentTiming } from "./useAppointmentTiming"
import type { UpcomingAppointment } from "./types"
import { Link as ReactRouterLink } from "react-router-dom"

/** How long the copy confirmation stays up, in ms. */
const COPY_CONFIRMATION_MS = 4000

/**
 * What the snackbar says either way. Named rather than inlined at the JSX branch only because the
 * failure sentence is long enough that the ternary reads better without it.
 */
const COPY_SUCCESS_MESSAGE = "Zoom link copied."
const COPY_FAILED_MESSAGE = "We couldn't copy the link. Use Join to open the visit instead."

/** Whether the copy affordance has something to say. */
type CopyState = "idle" | "copied" | "failed"

/**
 * Which primary action the card offers. Three variants, and imminence outranks outstanding forms:
 * a member five minutes out needs to join the call, not fill in a form.
 *
 * Resolved into a union and rendered through a `switch` rather than a lookup table, so every call
 * site stays static — CodeQL's `js/unvalidated-dynamic-method-call` fires on invoking a function
 * value chosen by external input, and no lookup structure avoids it. Same shape as
 * `getScenarioData` in `mock/scenarios.ts`.
 */
type UpNextAction = "join" | "completeForms" | "cancelOnly"

const resolveAction = (imminent: boolean, formsComplete: boolean): UpNextAction => {
  if (imminent) {
    return "join"
  }
  return formsComplete ? "cancelOnly" : "completeForms"
}

function PrimaryAction({
  action,
  joinUrl,
  canJoin,
  formsUrl
}: {
  action: UpNextAction
  joinUrl: string
  /** Whether a usable join link is available yet. False renders a disabled Join rather than a dead link. */
  canJoin: boolean
  formsUrl: string
}): JSX.Element | null {
  switch (action) {
    case "join":
      // No link yet (Healthie hasn't provisioned it): a disabled Join, not an anchor to nowhere.
      if (!canJoin) {
        return (
          <Button variant="contained" color="secondary" disabled>
            Join
          </Button>
        )
      }
      // A new tab: losing the app's state mid-navigation to join a call is the worse failure.
      return (
        <Button
          variant="contained"
          color="secondary"
          component="a"
          href={joinUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Join
        </Button>
      )
    case "completeForms":
      // `/telemedicine/booking/:id/forms`, gated behind the `telemedicineBookingFlow` flag — with
      // the flag off that route redirects home. The `:id` segment is currently ignored
      // by the page, which hardcodes `POC_FORM_ID` / `POC_USER_ID`; MEM-686 resolves the form from
      // the appointment. Passing the real id now means only that route has to change.
      return (
        <Button variant="contained" color="secondary" component={ReactRouterLink} to={formsUrl}>
          Complete forms
        </Button>
      )
    case "cancelOnly":
      return null
    default: {
      const unhandled: never = action
      throw new Error(`Unhandled Up next action: ${String(unhandled)}`)
    }
  }
}

/**
 * `onRequestCancel` rather than a dialog of its own: confirming a cancelation removes the
 * appointment, which unmounts this card, and frame 7 draws the canceled dialog over a page that has
 * already dropped its Up next section. The card asks; the page owns the flow. See
 * `CancelAppointmentDialog.tsx`.
 */
export function UpNextCard({
  appointment,
  onRequestCancel,
  bookingPath
}: {
  /**
   * Nullable, and so is its `date`. The page only lists this section for a scheduled appointment, but
   * the record is fixture data today and API data later; the guard below is what the host's
   * `strictNullChecks: false` used to leave implicit (see `UpNextCard.test.tsx`, which renders both).
   */
  appointment: UpcomingAppointment | null | undefined
  onRequestCancel: (appointment: UpcomingAppointment) => void
  /** The booking route, injected by the host — the package cannot reach its `Paths` enum. */
  bookingPath: string
}): JSX.Element | null {
  const theme = useTheme()
  const intl = useIntl()
  const [copyState, setCopyState] = useState<CopyState>("idle")
  // Read before the null guard below, because a hook cannot be called conditionally.
  const { now, imminent, joinUrl: resolvedJoinUrl, canJoin } = useAppointmentTiming(appointment)

  // Guard rather than take the page down with a missing appointment or a missing date on one.
  if (!appointment?.date) {
    return null
  }

  const { id, provider, appointmentType, date, formsComplete, status } = appointment
  const action = resolveAction(imminent, formsComplete)

  // `status` permits "canceled" and "completed", and nothing stops such a record reaching the
  // `upcoming` slot. Only a scheduled visit can be joined, prepared for or cancelled; whether the
  // card renders at all for the others is page composition, and belongs to CONAPP-270.
  const actionable = status === "scheduled"

  const providerName = formatProviderName(provider)
  const timeLabel = formatAppointmentTime(intl, date)
  const appointmentTypeLabel = getAppointmentTypeLabel(appointmentType)

  // Frames 2/3/5 read "Initial consult · August 31 at 10:30 AM"; frame 4 drops the "at" and adds the
  // weekday once the visit is imminent. The dates themselves still come from `intl` — `formatDate` and
  // `formatTime` are Intl wrappers with nothing to extract, so a member in a non-English locale still
  // gets their own date format even though the surrounding copy is English.
  const metaLabel = imminent
    ? `${appointmentTypeLabel} · ${formatAppointmentDateWithWeekday(intl, date)} · ${timeLabel}`
    : `${appointmentTypeLabel} · ${formatAppointmentDate(intl, date)} at ${timeLabel}`

  const copyJoinUrl = async () => {
    try {
      await navigator.clipboard.writeText(resolvedJoinUrl)
      setCopyState("copied")
    } catch {
      // An insecure context and a denied permission both land here. Claiming success when the
      // clipboard is empty is worse than saying it failed, so the state is only set on resolve.
      setCopyState("failed")
    }
  }

  return (
    <Stack sx={{ flexDirection: "column" }} spacing={theme.spacing(theme.tokens.spacing.md)}>
      <Typography variant="h3">Up next</Typography>
      <Paper
        elevation={0}
        sx={{
          // `.mob-card` keeps `position: relative` too, so this stays flat rather than becoming
          // another breakpoint object — even though on mobile there is no longer an absolutely
          // positioned child for it to anchor.
          position: "relative",
          // `.mob-card` adds `display: flex; flex-direction: column` so the in-flow chip can
          // `align-self: flex-start` and shrink-wrap. `flexDirection` is inert under
          // `display: block`, so only `display` needs the breakpoint.
          display: perViewport("flex", "block"),
          flexDirection: "column",
          padding: theme.spacing(theme.tokens.spacing.lg),
          borderRadius: theme.tokens.borderRadius.md
        }}
      >
        {/*
          The one element on the page whose LAYOUT changes between the two compositions rather than
          just its measurements. Desktop `.date-chip` is lifted out of flow into the card's
          top-inline-end corner on a `secondary.light` ground; frame 9's `.mob-chip` sits in flow above
          the lead on `background.hover` (`#F0F0F0`, which is the design's `--bg-hover` exactly). It is
          already the Paper's first child, so flow order needs no change — only the positioning does.

          The insets stay flat: they are inert under `position: static`, and spelling them as
          breakpoint objects would add two more media queries to say nothing.
        */}
        <Chip
          label={formatCountdownLabel(intl, date, now)}
          sx={{
            position: perViewport("static", "absolute"),
            alignSelf: "flex-start",
            insetBlockStart: theme.spacing(theme.tokens.spacing.lg),
            insetInlineEnd: theme.spacing(theme.tokens.spacing.lg),
            marginBlockEnd: perViewport(
              theme.spacing(theme.tokens.spacing.md),
              theme.spacing(theme.tokens.spacing.none)
            ),
            backgroundColor: perViewport(theme.palette.background.hover, theme.palette.secondary.light),
            paddingInline: theme.spacing(theme.tokens.spacing.xs)
          }}
        />
        {/*
          `.lead` is `align-items: flex-start`; frame 9's `.mob-lead` centres the portrait against the
          two lines of text instead, which is what keeps a 48px avatar from sitting proud of them.
        */}
        <Stack
          sx={{ flexDirection: "row", alignItems: perViewport("center", "flex-start") }}
          spacing={theme.spacing(theme.tokens.spacing.md)}
        >
          {/* No `size`: the component default IS the appointment-row size, 54px desktop / 48px mobile. */}
          <AppointmentsAvatar name={provider.name} photoUrl={provider.photoUrl} />
          <Box sx={{ minInlineSize: 0 }}>
            {/* `.nm` → `.mob-nm`, h5 → h6. See `perViewportTypography` for why `variant` stays. */}
            <Typography variant="h5" sx={{ typography: perViewportTypography("h6", "h5") }}>
              {providerName}
            </Typography>
            {/* `body1` is already `text.secondary` in the Carotene theme, matching the design's `.mt`. */}
            <Typography sx={{ typography: perViewportTypography("body2", "body1") }}>{metaLabel}</Typography>
            {imminent && actionable && canJoin && (
              <Link
                component="button"
                type="button"
                onClick={copyJoinUrl}
                sx={{ marginBlockStart: theme.spacing(theme.tokens.spacing.xs) }}
              >
                Copy Zoom link
              </Link>
            )}
          </Box>
        </Stack>
        {actionable && (
          /*
            `.mob-acts` is a column at `align-items: stretch`, which is what makes the primary CTA full
            width — the `Button` is `inline-flex` and stretching is the cross-axis default, so it needs
            no `inlineSize` of its own. The theme sets `useFlexGap: true` on every `Stack`, so
            `spacing` emits `gap` rather than a margin keyed to the `direction` prop, which is why
            overriding `flexDirection` through `sx` stays correct in both directions.
          */
          <Stack
            sx={{
              flexDirection: perViewport("column", "row"),
              alignItems: perViewport("stretch", "center"),
              marginBlockStart: theme.spacing(theme.tokens.spacing.lg),
              /*
                `gap` in `sx` rather than the `spacing` prop, because the two compositions disagree
                about where the separation LIVES. `.acts` is a 24px gap; `.mob-acts` is `gap: 0` and
                lets `.mob-cancel-link`'s own 16px block padding do the work, so the tap target grows
                instead of the space between two of them. Expressing that needs a real zero here and a
                padding below, which `spacing` alone cannot say.
              */
              gap: perViewport(theme.spacing(theme.tokens.spacing.none), theme.spacing(theme.tokens.spacing.lg))
            }}
          >
            <PrimaryAction
              action={action}
              joinUrl={resolvedJoinUrl}
              canJoin={canJoin}
              formsUrl={`${bookingPath}/${id}/forms`}
            />
            {/* `.mob-cancel-link { align-self: center }` — centred under the CTA rather than stretched. */}
            <Link
              component="button"
              type="button"
              onClick={() => onRequestCancel(appointment)}
              sx={{
                alignSelf: perViewport("center", "auto"),
                paddingBlock: perViewport(
                  theme.spacing(theme.tokens.spacing.md),
                  theme.spacing(theme.tokens.spacing.none)
                )
              }}
            >
              Cancel
            </Link>
          </Stack>
        )}
      </Paper>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={copyState !== "idle"}
        autoHideDuration={COPY_CONFIRMATION_MS}
        onClose={() => setCopyState("idle")}
      >
        <Alert severity={copyState === "failed" ? "error" : "success"} onClose={() => setCopyState("idle")}>
          {copyState === "failed" ? COPY_FAILED_MESSAGE : COPY_SUCCESS_MESSAGE}
        </Alert>
      </Snackbar>
    </Stack>
  )
}
